import { ArrowRight, Clock, FileText, Plus } from 'lucide-react';
import { Skeleton } from '@/shared/components/ui/skeleton';
import type { FpQaEngagementSummary } from '../../types/fp-qa.types';

type SavedEngagementsListProps = {
  engagements: FpQaEngagementSummary[];
  isLoading: boolean;
  onStartNew: () => void;
  onOpenEngagement: (id: string) => void;
};

const formatLongDate = (iso: string): string => {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
};

export const SavedEngagementsList = ({
  engagements,
  isLoading,
  onStartNew,
  onOpenEngagement,
}: SavedEngagementsListProps) => {
  const mostRecent = engagements[0];

  return (
    <section className="space-y-8">
      <header className="space-y-1">
        <h2 className="font-termina font-medium text-[28px] leading-tight text-text-primary">
          Care Guides
        </h2>
        <p className="text-sm text-text-secondary">
          Continue your client&apos;s care planning journey or start a new plan.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <HeroStartCard onClick={onStartNew} />
        <HeroContinueCard
          disabled={!mostRecent}
          onClick={() => mostRecent && onOpenEngagement(mostRecent.id)}
        />
      </div>

      <div className="space-y-4">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.06em] text-text-tertiary">
          Client Care Guides
        </h3>

        {isLoading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-24 w-full rounded-xl" />
            ))}
          </div>
        ) : engagements.length === 0 ? (
          <div className="rounded-xl border border-border-subtle bg-card-subtle px-6 py-10 text-center">
            <h4 className="font-termina font-medium text-lg text-text-primary">No care guides yet</h4>
            <p className="mx-auto mt-1 max-w-md text-sm text-text-secondary">
              Start a new care guide and we&apos;ll guide you through a short Q&amp;A, then assemble
              your client&apos;s matched providers and resources here.
            </p>
          </div>
        ) : (
          <ul className="space-y-3">
            {engagements.map((engagement) => (
              <li key={engagement.id}>
                <PlanRow
                  engagement={engagement}
                  onClick={() => onOpenEngagement(engagement.id)}
                  formatDate={formatLongDate}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

const HeroStartCard = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex flex-col items-start gap-2 rounded-2xl border border-sage bg-sage p-6 text-left text-white transition-transform duration-150 ease-out hover:-translate-y-0.5 hover:shadow-[0_2px_8px_rgba(15,23,42,0.10)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
  >
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white">
      <Plus className="h-5 w-5" aria-hidden />
    </span>
    <h3 className="font-termina font-medium text-xl">Start a New Care Guide</h3>
    <p className="text-sm text-white/80">
      Our AI Navigator will guide you through creating a personalized care guide for your client.
    </p>
    <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-amber-200 px-3 py-1 text-sm font-medium text-amber-900 transition-colors duration-150 ease-out group-hover:bg-amber-300">
      Begin now
      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
    </span>
  </button>
);

const HeroContinueCard = ({ onClick, disabled }: { onClick: () => void; disabled: boolean }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="group flex flex-col items-start gap-2 rounded-2xl border border-border-subtle bg-card-base p-6 text-left text-text-primary transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-sage hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:border-border-subtle disabled:hover:shadow-none"
  >
    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-sage-bg text-sage">
      <FileText className="h-5 w-5" aria-hidden />
    </span>
    <h3 className="font-termina font-medium text-xl">Continue Client&apos;s Guide</h3>
    <p className="text-sm text-text-secondary">
      Resume working on your client&apos;s most recent care guide.
    </p>
    <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-sage">
      View plan
      <ArrowRight className="h-3.5 w-3.5" aria-hidden />
    </span>
  </button>
);

type PlanRowProps = {
  engagement: FpQaEngagementSummary;
  onClick: () => void;
  formatDate: (iso: string) => string;
};

const PlanRow = ({ engagement, onClick, formatDate }: PlanRowProps) => {
  // Prefer the FP-chosen plan name; fall back to the care stage label.
  const title =
    engagement.name?.trim() || engagement.careStageLabel || 'Care Guide';
  const forName = engagement.clientName?.trim();
  // When the plan has a custom name, surface the care stage as secondary
  // context so the FP can still see which stage drove it.
  const stageContext =
    engagement.name?.trim() && engagement.careStageLabel
      ? engagement.careStageLabel
      : null;
  const description = engagement.providerCategoryChips.slice(0, 3).join(' · ');

  return (
    <button
      type="button"
      onClick={onClick}
      className="group flex w-full items-center gap-4 rounded-xl border border-border-subtle bg-card-base px-5 py-4 text-left transition-all duration-150 ease-out hover:-translate-y-0.5 hover:border-sage hover:shadow-[0_2px_8px_rgba(15,23,42,0.06)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-emphasis"
    >
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="font-termina font-medium text-base text-text-primary">{title}</span>
          {forName && (
            <span className="text-sm text-text-secondary">for {forName}</span>
          )}
          {stageContext && (
            <span className="text-xs text-text-tertiary">· {stageContext}</span>
          )}
        </div>
        {description && (
          <p className="line-clamp-1 text-sm text-text-secondary">{description}</p>
        )}
        <div className="flex items-center gap-1.5 text-xs text-text-tertiary">
          <Clock className="h-3 w-3" aria-hidden />
          Created {formatDate(engagement.completedAt)}
        </div>
      </div>
      <span className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-sage transition-colors duration-150 ease-out group-hover:text-[hsl(169_95%_18%)]">
        View
        <ArrowRight className="h-3.5 w-3.5" aria-hidden />
      </span>
    </button>
  );
};
